<script setup lang="ts">
import FileImage from '@/core/files/FileImage.vue';
import { EntityAppearanceType } from '@/api/generated/api.enum';
import { COLOR_WHITE } from '@/constants';
import AnyIcon from '@/core/elements/AnyIcon.vue';
import { hexToRgba } from '@/utils/color.utils';
import { computed, ref, watch } from 'vue';
import { APPEARANCE_ICONS } from './appearance.constants';
import type { TAppearanceSize, TEntityAppearance } from './entityAppearance.types';
import { useAppearanceImageSource } from '@/composables/useEntityAppearance';

const props = withDefaults(
  defineProps<{
    abbreviation: string;
    appearance: TEntityAppearance;
    size?: TAppearanceSize;
  }>(),
  { size: 'md' },
);

const sizeClasses: Record<TAppearanceSize, string> = {
  sm: 'size-icon-button text-xs',
  md: 'size-icon-xl text-sm',
  lg: 'size-[56px] text-lg',
};
const imageSource = useAppearanceImageSource(() =>
  props.appearance.type === EntityAppearanceType.image ? props.appearance.source : undefined,
);
const imageFailed = ref(false);
watch(imageSource, () => {
  imageFailed.value = false;
});
const emoji = computed(() => (props.appearance.type === EntityAppearanceType.emoji ? props.appearance.unicode : ''));
const icon = computed(() =>
  props.appearance.type === EntityAppearanceType.icon && APPEARANCE_ICONS.includes(props.appearance.key)
    ? props.appearance.key
    : null,
);
const foreground = computed(() => {
  if (
    props.appearance.type === EntityAppearanceType.image ||
    props.appearance.backgroundColor.toUpperCase() === COLOR_WHITE
  )
    return undefined;
  return props.appearance.backgroundColor;
});
const background = computed(() => (foreground.value ? hexToRgba(foreground.value, 0.18) : COLOR_WHITE));
</script>

<template>
  <span
    class="inline-flex shrink-0 items-center justify-center overflow-hidden rounded-md border border-divider font-semibold text-primary"
    :class="sizeClasses[size]"
    :style="{ backgroundColor: background, color: foreground }"
  >
    <FileImage
      v-if="imageSource && !imageFailed"
      :source="imageSource"
      alt=""
      placeholder-size="sm"
      class="size-full object-cover"
      @error="imageFailed = true"
    />
    <span
      v-else-if="emoji"
      class="h-auto max-w-[24px] font-emoji text-[length:min(1.6em,24px)] font-normal leading-none"
      >{{ emoji }}</span
    >
    <AnyIcon v-else-if="icon" :icon-name="icon" aria-hidden="true" class="h-auto w-1/2 max-w-[24px]" />
    <span v-else class="max-w-full truncate px-xs text-body">{{ abbreviation || '–' }}</span>
  </span>
</template>
